import { Loading } from './Loading';
import { useMasterDocumentTranslation } from '../../i18n';

/**
 * Spinner de campo, idéntico al que `LineFormModal` y `MasterDocumentFormModal`
 * tenían escrito a mano (mismo path, mismas opacidades, mismas clases).
 *
 * ⚠️ **Transitorio.** Existe solo para migrar esos dos sitios a `Loading` sin
 * cambiar un pixel (opción A de `LOADING-INTEGRATION.md` §8.2). Cuando diseño
 * valide unificar la paleta del feature (opción B), este icono se borra y
 * `FieldLoadingIndicator` deja de pasar `icon`, quedándose con el `ArrowPathIcon`
 * por defecto de `Loading`.
 *
 * ⚠️ `text-primary-custom-500` **no existe** como token del kit — `src/index.css`
 * define `dark-bg-custom`, `dark-content-custom` y `dark-border-custom`, ninguna
 * variante `primary-custom`. Se conserva tal cual estaba para no cambiar el
 * aspecto, pero en modo claro no pinta: el spinner hereda el color del texto.
 * Es un bug preexistente y transversal (34 archivos del repo usan esa clase), no
 * de este componente.
 */
function FieldSpinnerIcon() {
  return (
    <svg
      className="w-3.5 h-3.5 animate-spin text-primary-custom-500 dark:text-dark-border-custom"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

/**
 * Indicador de "este campo se está recalculando en el servidor", anclado a la
 * esquina superior derecha del campo.
 *
 * Lo dispara `evaluateServerRule`: el usuario cambia un campo observado por una
 * regla de servidor y el valor destino tarda en llegar. Ambos modales de
 * formulario lo pintaban con el MISMO bloque de 16 líneas copiado; aquí vive una
 * sola vez.
 *
 * A diferencia de `Loading`, este componente **sí traduce**: es de feature, no un
 * primitivo, y sus dos consumidores usan el mismo catálogo del core. El
 * contenedor posicionado lleva el `title` (tooltip nativo para ratón) y `Loading`
 * aporta la semántica accesible (`role="status"` + `aria-live` + `aria-label`),
 * que el bloque original no tenía. El `title` va fuera a propósito: en el mismo
 * elemento que el `aria-label` competiría con él como nombre accesible.
 *
 * @see Loading — el primitivo indeterminado
 * @see MasterDocument/LOADING-INTEGRATION.md — plan de migración (punto P2)
 */
export function FieldLoadingIndicator() {
  const { t } = useMasterDocumentTranslation();

  return (
    <span
      className="absolute top-0 right-0 flex items-center justify-center w-5 h-5"
      title={t('master_document.chrome.updatingTitle')}
    >
      <Loading
        size="sm"
        layout="inline"
        ariaLabel={t('master_document.chrome.updating')}
        icon={<FieldSpinnerIcon />}
      />
    </span>
  );
}
